import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  accionsApi,
  detallNota,
  esOberta,
  MIDA_MAX_TEXT,
  TIPUS_IMATGE,
  useAccioActivacio,
  useCercaUsuaris,
  useImatgeAccio,
  useSilenci,
  validaImatge,
  type AccioDto,
  type ActivacioDto,
  type MencioDto,
  type MissatgeDto,
} from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import { Icona } from '@/components/Icona';
import { TextMarkdown } from '@/components/TextMarkdown';
import { useObjectUrl } from '@/components/useObjectUrl';
import { comprimeixImatge } from '@/utils/comprimeixImatge';
import { useFormat } from '@/i18n/useFormat';
import { useMe } from '@/permisos/MeContext';
import { Avatar, Buit } from './comu';
import {
  embolcalla,
  prefixaLinies,
  type EstatText,
  type MarcaInline,
  type TipusLlista,
} from './formatMarkdown';
import { tokenMencio, trossejaMencions, useEntradesXat } from './suport';

/** Text d'un missatge amb Markdown i mencions destacades (`@Nom`). */
export function TextMissatge({ text, mencions = [] }: { text: string; mencions?: string[] }) {
  return (
    <TextMarkdown
      text={text}
      renderText={(tros, clau) =>
        trossejaMencions(tros, mencions).map((p, j) =>
          p.mencio ? (
            <span key={`${clau}-${j}`} className="mencio">
              {p.text}
            </span>
          ) : (
            <span key={`${clau}-${j}`}>{p.text}</span>
          ),
        )
      }
    />
  );
}

/** Visor a pantalla completa d'una imatge del xat (Escape o clic fora per tancar). */
export function VisorImatge({
  url,
  alt,
  onTanca,
}: {
  url: string;
  alt: string;
  onTanca: () => void;
}) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const tanca = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    tanca.current?.focus();
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onTanca();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onTanca]);
  return (
    <div className="visor" onClick={onTanca}>
      <div
        className="visor__cos"
        role="dialog"
        aria-modal="true"
        aria-label={t('detall.imatge.visor')}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={tanca}
          type="button"
          className="visor__tanca"
          aria-label={tc('accions.tancar')}
          title={tc('accions.tancar')}
          onClick={onTanca}
        >
          <Icona nom="xmark" />
        </button>
        <img src={url} alt={alt} />
      </div>
    </div>
  );
}

/** Miniatura d'una imatge del xat, descarregada amb el token; en clicar s'obre el visor. */
export function ImatgeAccio({
  activacioId,
  accio,
  autor,
}: {
  activacioId: number;
  accio: AccioDto;
  autor: string;
}) {
  const { t } = useTranslation('operacio');
  const imatge = useImatgeAccio(activacioId, accio.id);
  const url = useObjectUrl(imatge.data);
  const [obert, setObert] = useState(false);
  const alt = detallNota(accio).adjuntNom || t('detall.imatge.alt', { autor });
  if (imatge.isError) {
    return (
      <div className="xat__imatge xat__imatge--error" role="note">
        <Icona nom="camera" /> {t('detall.imatge.errorCarrega')}
      </div>
    );
  }
  if (!url) {
    return (
      <div className="xat__imatge xat__imatge--carregant" aria-busy="true">
        {t('detall.imatge.carregant')}
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        className="xat__imatge"
        aria-label={t('detall.imatge.obre')}
        title={t('detall.imatge.obre')}
        onClick={() => setObert(true)}
      >
        <img src={url} alt={alt} data-testid="imatge-xat" />
      </button>
      {obert ? <VisorImatge url={url} alt={alt} onTanca={() => setObert(false)} /> : null}
    </>
  );
}

/** Missatge del xat desat com a acció NOTA (text Markdown i, opcionalment, una imatge). */
export function EvNota({
  activacioId,
  ac,
  meva,
}: {
  activacioId: number;
  ac: AccioDto;
  meva: boolean;
}) {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const d = detallNota(ac);
  const autor = meva ? t('detall.tu') : (ac.usuari ?? '—');
  const text = typeof d.text === 'string' ? d.text : '';
  return (
    <li
      className={`ev xat__msg${meva ? ' xat__msg--meu' : ''}`}
      data-accio="NOTA"
      data-imatge={d.adjuntId != null ? 'si' : undefined}
    >
      <Avatar nom={ac.usuari} color={meva ? 'bg-brand' : undefined} />
      <div className="ev__cos ev__cos--missatge">
        <div className="bubble bubble--in">
          {d.adjuntId != null ? (
            <ImatgeAccio activacioId={activacioId} accio={ac} autor={ac.usuari ?? '—'} />
          ) : null}
          {text ? <TextMissatge text={text} /> : null}
        </div>
        <span className="dim t-caption">
          {autor} · {f.hora(ac.at)}
        </span>
      </div>
    </li>
  );
}

/** Missatge de la conversa (`/missatges`): bombolla, o línia informativa si és del sistema. */
export function EvMissatge({ m, meva }: { m: MissatgeDto; meva: boolean }) {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const tipus = t(`detall.tipusMissatge.${m.tipus}`, {
    defaultValue: t('detall.tipusMissatge.TEXT'),
  });
  if (m.tipus === 'SISTEMA' || m.tipus === 'FINALITZACIO') {
    return (
      <li className="ev" data-tipus={m.tipus}>
        <span className="ev__punt">
          <Icona nom="info-circle" />
        </span>
        <div className="ev__cos">
          <span>
            <TextMissatge text={m.text} mencions={(m.mencions ?? []).map((x) => x.nom)} />
          </span>
          <span className="dim t-caption">
            {tipus}
            {m.at ? ` · ${f.hora(m.at)}` : ''}
          </span>
        </div>
      </li>
    );
  }
  const critic = m.tipus === 'RISC' || m.tipus === 'AFECTACIO' || m.prioritatAlta;
  const autor = meva ? t('detall.tu') : (m.autor ?? '—');
  return (
    <li className={`ev xat__msg${meva ? ' xat__msg--meu' : ''}`} data-tipus={m.tipus}>
      <Avatar nom={m.autor} color={meva ? 'bg-brand' : undefined} />
      <div className="ev__cos ev__cos--missatge">
        <div className={`bubble ${critic ? 'bubble--critic' : 'bubble--in'}`}>
          {critic ? (
            <div className="xat__tipus">
              <Icona nom="exclamationmark-triangle" />
              {tipus}
            </div>
          ) : m.tipus !== 'TEXT' ? (
            <div className="bubble__who">{tipus}</div>
          ) : null}
          <TextMissatge text={m.text} mencions={(m.mencions ?? []).map((x) => x.nom)} />
        </div>
        <span className="dim t-caption">
          {autor}
          {m.at ? ` · ${f.hora(m.at)}` : ''}
        </span>
      </div>
    </li>
  );
}

/**
 * Silencia / reactiva les notificacions dels missatges del xat d'aquesta activació per a
 * l'usuari actual. No afecta les alertes ni els escalats. `sufix` distingeix les instàncies
 * (capçalera del detall i pestanya Xat) perquè els identificadors no es repeteixin.
 */
export function BotoSilenci({ a, sufix = '' }: { a: ActivacioDto; sufix?: string }) {
  const { t } = useTranslation('operacio');
  const missatgeError = useMissatgeError();
  const silenci = useSilenci(a.id);
  const silenciada = Boolean(a.silenciada);
  const explicacio = t(
    silenciada ? 'detall.silenci.explicacioReactivar' : 'detall.silenci.explicacioSilenciar',
  );
  const idDesc = `silenci-${a.id}${sufix ? `-${sufix}` : ''}`;
  return (
    <>
      <button
        type="button"
        className={`btn${sufix ? ' btn--s' : ''}${silenciada ? ' btn--silenciada' : ''}`}
        title={explicacio}
        aria-describedby={idDesc}
        data-testid={`boto-silenci${sufix ? `-${sufix}` : ''}`}
        data-silenciada={silenciada ? 'si' : 'no'}
        disabled={silenci.isPending}
        onClick={() => silenci.mutate(!silenciada)}
      >
        <Icona nom={silenciada ? 'bell' : 'bell-slash'} />
        {t(silenciada ? 'detall.silenci.reactivar' : 'detall.silenci.silenciar')}
      </button>
      <span id={idDesc} hidden>
        {explicacio}
      </span>
      {silenci.error ? (
        <span className="error-text" role="alert">
          {missatgeError(silenci.error)}
        </span>
      ) : null}
    </>
  );
}

const GLIFS = { negreta: 'B', cursiva: 'I', ratllat: 'S', numerada: '1.', codi: '</>' } as const;

type BotoFormat =
  | { clau: string; marca: MarcaInline; contingut: ReactNode }
  | { clau: string; llista: TipusLlista; contingut: ReactNode };

const BOTONS_FORMAT: BotoFormat[] = [
  { clau: 'negreta', marca: '**', contingut: <strong>{GLIFS.negreta}</strong> },
  { clau: 'cursiva', marca: '*', contingut: <em>{GLIFS.cursiva}</em> },
  { clau: 'ratllat', marca: '~~', contingut: <s>{GLIFS.ratllat}</s> },
  { clau: 'llista', llista: 'pics', contingut: <Icona nom="list-bullet" /> },
  { clau: 'llistaNumerada', llista: 'numerada', contingut: <span>{GLIFS.numerada}</span> },
  { clau: 'codi', marca: '`', contingut: <span className="mono">{GLIFS.codi}</span> },
];

/** Barra de format del xat: negreta, cursiva, ratllat, llistes i codi. */
export function BarraFormat({ onAplica }: { onAplica: (f: (e: EstatText) => EstatText) => void }) {
  const { t } = useTranslation('operacio');
  return (
    <div className="barra-format" role="toolbar" aria-label={t('detall.format.barra')}>
      {BOTONS_FORMAT.map((b) => (
        <button
          key={b.clau}
          type="button"
          className="barra-format__boto"
          aria-label={t(`detall.format.${b.clau}`)}
          title={t(`detall.format.${b.clau}`)}
          // Es manté el focus (i la selecció) a l'àrea de text.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            onAplica((e) => ('marca' in b ? embolcalla(e, b.marca) : prefixaLinies(e, b.llista)))
          }
        >
          {b.contingut}
        </button>
      ))}
    </div>
  );
}

function primeraImatge(files: FileList | null | undefined): File | null {
  if (!files) return null;
  for (const f of Array.from(files)) if (f.type.startsWith('image/') || f.type === '') return f;
  return files[0] ?? null;
}

/**
 * Caixa per escriure a la conversa de l'activació: barra de format (Markdown), mencions
 * (@persona, servei, departament) i imatges (botó, enganxar o arrossegar). Intro envia;
 * Maj+Intro fa un salt de línia.
 */
export function Composer({ a }: { a: ActivacioDto }) {
  const { t } = useTranslation('operacio');
  const missatgeError = useMissatgeError();
  const [text, setText] = useState('');
  const [mencions, setMencions] = useState<MencioDto[]>([]);
  const [imatge, setImatge] = useState<File | null>(null);
  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  const [arrossegant, setArrossegant] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const selector = useRef<HTMLInputElement>(null);
  const previa = useObjectUrl(imatge);
  const token = tokenMencio(text);
  const usuaris = useCercaUsuaris(token ?? '');
  const envia = useAccioActivacio(a.id, () => {
    const vigents = mencions.filter((m) => text.includes(`@${m.nom}`));
    return accionsApi.missatge(a.id, { tipus: 'TEXT', text: text.trim(), mencions: vigents });
  });
  const enviaImatge = useAccioActivacio(a.id, (f: File) => accionsApi.imatge(a.id, f, text));
  const pendent = envia.isPending || enviaImatge.isPending;
  const error = envia.error ?? enviaImatge.error;

  const candidats: MencioDto[] =
    token == null || imatge
      ? []
      : [
          ...(usuaris.data ?? []).map((u) => ({
            tipus: 'USUARI' as const,
            id: u.id,
            nom: [u.nom, u.cognoms].filter(Boolean).join(' '),
          })),
          ...(a.serveiIds[0] != null && a.servei
            ? [{ tipus: 'SERVEI' as const, id: a.serveiIds[0], nom: a.servei }]
            : []),
          ...(a.departamentId != null && a.departament
            ? [{ tipus: 'DEPARTAMENT' as const, id: a.departamentId, nom: a.departament }]
            : []),
        ].filter((c) => c.nom.toLowerCase().includes(token.toLowerCase()));

  const tria = (c: MencioDto) => {
    setText((s) => s.replace(/@([^\s@]*)$/, `@${c.nom} `));
    setMencions((ms) => (ms.some((m) => m.tipus === c.tipus && m.id === c.id) ? ms : [...ms, c]));
  };

  const aplica = (f: (e: EstatText) => EstatText) => {
    const el = area.current;
    const nou = f({
      valor: text,
      inici: el?.selectionStart ?? text.length,
      fi: el?.selectionEnd ?? text.length,
    });
    setText(nou.valor.slice(0, MIDA_MAX_TEXT));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(nou.inici, nou.fi);
    });
  };

  // Es comprimeix abans de validar la mida: una foto gran que en comprimir-se queda per sota del
  // límit s'accepta. Si en trien una altra mentre es comprimeix, el resultat antic es descarta.
  const [comprimint, setComprimint] = useState(false);
  const darreraTria = useRef(0);
  const triaFitxer = async (original: File | null) => {
    if (!original) return;
    const tria = ++darreraTria.current;
    setComprimint(true);
    const f = await comprimeixImatge(original);
    if (tria !== darreraTria.current) return;
    setComprimint(false);
    const problema = validaImatge(f);
    if (problema) {
      setErrorLocal(
        t(problema === 'tipus' ? 'detall.imatge.tipusNoPermes' : 'detall.imatge.massaGran'),
      );
      return;
    }
    setErrorLocal(null);
    setImatge(f);
  };

  const enviar = () => {
    // Mentre s'envia un missatge no se n'accepta cap altre (evita duplicats amb Intro repetit).
    if (pendent || comprimint || (!text.trim() && !imatge)) return;
    const neteja = () => {
      setText('');
      setMencions([]);
      setImatge(null);
    };
    if (imatge) enviaImatge.mutate(imatge, { onSuccess: neteja });
    else envia.mutate(undefined, { onSuccess: neteja });
  };

  const tecla = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.altKey && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      aplica((s) => embolcalla(s, '**'));
    } else if (mod && !e.altKey && (e.key === 'i' || e.key === 'I')) {
      e.preventDefault();
      aplica((s) => embolcalla(s, '*'));
    } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const enganxa = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const f = primeraImatge(e.clipboardData?.files);
    if (f && f.type.startsWith('image/')) {
      e.preventDefault();
      void triaFitxer(f);
    }
  };

  const arrossega = (e: DragEvent<HTMLFormElement>) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    setArrossegant(true);
  };

  const deixa = (e: DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setArrossegant(false);
    void triaFitxer(primeraImatge(e.dataTransfer?.files));
  };

  return (
    <>
      {error ? (
        <p className="error-text" role="alert">
          {missatgeError(error)}
        </p>
      ) : null}
      {errorLocal ? (
        <p className="error-text" role="alert" data-testid="error-imatge">
          {errorLocal}
        </p>
      ) : null}
      <form
        className={`composer composer--xat${arrossegant ? ' composer--arrossega' : ''}`}
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
        onDragOver={arrossega}
        onDragEnter={arrossega}
        onDragLeave={() => setArrossegant(false)}
        onDrop={deixa}
      >
        {candidats.length > 0 ? (
          <div className="suggeriments" role="listbox" aria-label={t('detall.mencions')}>
            {candidats.map((c) => (
              <button
                key={`${c.tipus}-${c.id}`}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => tria(c)}
              >
                {c.nom} <span className="dim">{t(`detall.tipusMencio.${c.tipus}`)}</span>
              </button>
            ))}
          </div>
        ) : null}
        <div className="composer__cap">
          <BarraFormat onAplica={aplica} />
          <button
            type="button"
            className="barra-format__boto"
            aria-label={t('detall.imatge.adjunta')}
            title={t('detall.imatge.adjunta')}
            onClick={() => selector.current?.click()}
          >
            <Icona nom="paperclip" />
          </button>
          <input
            ref={selector}
            type="file"
            hidden
            accept={TIPUS_IMATGE.join(',')}
            data-testid="selector-imatge"
            onChange={(e) => {
              void triaFitxer(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <span className="spacer" />
          <span className="t-caption-2 dim composer__ajuda">{t('detall.ajudaComposer')}</span>
        </div>
        {imatge ? (
          <div className="composer__adjunt" data-testid="previa-imatge">
            {previa ? <img src={previa} alt="" /> : <Icona nom="camera" />}
            <span className="truncate">{imatge.name}</span>
            <button
              type="button"
              className="modal__tanca"
              aria-label={t('detall.imatge.treu')}
              title={t('detall.imatge.treu')}
              onClick={() => setImatge(null)}
            >
              <Icona nom="xmark" />
            </button>
          </div>
        ) : null}
        {arrossegant ? (
          <div className="composer__deixa" aria-hidden="true">
            {t('detall.imatge.deixaAnar')}
          </div>
        ) : null}
        <div className="composer__fila">
          <textarea
            ref={area}
            className="input composer__input"
            aria-label={imatge ? t('detall.peuImatge') : t('detall.escriuMissatge')}
            placeholder={imatge ? t('detall.peuImatge') : t('detall.escriuMissatge')}
            rows={2}
            maxLength={MIDA_MAX_TEXT}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={tecla}
            onPaste={enganxa}
          />
          <button
            type="submit"
            className="btn btn--primary"
            disabled={(!text.trim() && !imatge) || pendent || comprimint}
          >
            <Icona nom="paperplane" />
            {t('comu:accions.enviar')}
          </button>
        </div>
      </form>
    </>
  );
}

/**
 * Pestanya Xat del detall: la conversa de l'activació (missatges i imatges, amb Markdown i
 * mencions) i la caixa per escriure a sota. Si l'activació és tancada, el xat és de només
 * lectura. Es desplaça al missatge més recent en obrir la pestanya i quan n'arriba un de nou.
 */
export function PanellXat({ a, actiu }: { a: ActivacioDto; actiu: boolean }) {
  const { t } = useTranslation('operacio');
  const { me } = useMe();
  const { entrades } = useEntradesXat(a.id);
  const llista = useRef<HTMLOListElement>(null);
  const ultima = entrades.at(-1)?.clau;
  const oberta = esOberta(a.estat);
  // Si l'usuari és al final de la conversa, s'hi manté quan la llista canvia d'alçada (la
  // conversa ocupa l'espai que deixen la capçalera i la caixa per escriure, que poden créixer).
  const alFinal = useRef(true);
  useEffect(() => {
    const el = llista.current;
    if (actiu && el) {
      el.scrollTop = el.scrollHeight;
      alFinal.current = true;
    }
  }, [actiu, ultima, entrades.length]);
  useEffect(() => {
    const el = llista.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const desa = () => {
      alFinal.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    };
    const obs = new ResizeObserver(() => {
      if (alFinal.current) el.scrollTop = el.scrollHeight;
    });
    obs.observe(el);
    el.addEventListener('scroll', desa, { passive: true });
    return () => {
      obs.disconnect();
      el.removeEventListener('scroll', desa);
    };
  }, []);
  const silenciada = Boolean(a.silenciada);
  return (
    <div className="xat" data-panel="xat">
      <div className="xat__cap">
        <span className="t-footnote dim spacer">
          {t(
            silenciada
              ? 'detall.silenci.explicacioReactivar'
              : 'detall.silenci.explicacioSilenciar',
          )}
        </span>
        <BotoSilenci a={a} sufix="xat" />
      </div>
      <ol
        ref={llista}
        className="xat__llista"
        aria-live="polite"
        aria-label={t('detall.xat.conversa')}
        tabIndex={0}
      >
        {entrades.length === 0 ? (
          <li>
            <Buit icona="message">{t('detall.xat.buit')}</Buit>
          </li>
        ) : (
          entrades.map((e) =>
            'accio' in e ? (
              <EvNota
                key={e.clau}
                activacioId={a.id}
                ac={e.accio}
                meva={me != null && e.accio.usuariId != null && e.accio.usuariId === me.id}
              />
            ) : (
              <EvMissatge
                key={e.clau}
                m={e.missatge}
                meva={me != null && e.missatge.autorId === me.id}
              />
            ),
          )
        )}
      </ol>
      {oberta ? (
        <Composer a={a} />
      ) : (
        <p className="xat__lectura t-footnote dim" role="note">
          <Icona nom="lock" /> {t('detall.xat.nomesLectura')}
        </p>
      )}
    </div>
  );
}
